/**
 * Editing one component — course component or library component, one mechanism.
 *
 * Verawood removed the server-rendered editor from some block types and not others, and
 * nothing in the platform declares which. Rather than keep a list of type names that would
 * be right today and silently wrong after an upgrade, the server probes the block and tells
 * us. See `backend/openedx_thinlms_authoring/editors.py`.
 *
 * The probe is the same call for both kinds of component. What differs is WHERE the
 * platform keeps a block, and that is a fact about the key rather than a guess about the
 * block:
 *
 *   block-v1:…   a course component, in the modulestore. Its editor is Studio's own editor
 *                page, and its writes go through Studio's block handler (our
 *                `/api/authoring/v1/xblock/…`, which wraps it).
 *   lb:…         a library component, in Learning Core. Its editor is the v2 XBlock
 *                runtime's iframe embed, and its writes go through that runtime's fields
 *                endpoint — the same two the official authoring MFE uses for a library
 *                block (`src/library-authoring/LibraryBlock` and
 *                `src/editors/data/services/cms/urls.ts`).
 *
 * So both surfaces call `getEditor`, `editorEmbedUrl` and `saveBlockContent` with nothing
 * but a key, and the difference stays inside this file.
 */

import { openedx } from './openedx';
import { editorUrl } from './unit';

export type EditorKind = 'studio' | 'html' | 'olx' | 'video' | 'question';

/** Multiple choice and checkboxes. The only two shapes v1 of the form can represent. */
export type QuestionType = 'multiple_choice' | 'checkboxes';

export interface QuestionOption {
  text: string;
  correct: boolean;
  /** Which element held the text, so the encoder puts it back in the same one. */
  carrier: null | 'div';
}

/**
 * The encoder's bookkeeping. ROUND-TRIPPED UNTOUCHED AND UNREAD.
 *
 * This is what makes "opened a question, changed nothing, saved" a genuine no-op rather than
 * a silent reformat: the indent the source used, whether it ended in a newline, which of the
 * two spellings of an empty root it was, and the root element's own attributes. Two problems
 * in this estate carry `display_name` on `<problem>`; dropping it would be a silent edit and
 * reading it would give the component two names, so it travels through the form and back
 * without the form ever looking at it.
 */
export interface QuestionOlx {
  attributes: Record<string, string>;
  group_tag: string | null;
  group_type: string | null;
  indent: string | null;
  trailing_newline: boolean;
  expanded_empty_root: boolean;
}

export interface Question {
  /** `null` only for the empty question — a `<problem>` with nothing in it. */
  type: QuestionType | null;
  prompt: { text: string; carrier: 'label' | 'div' | 'p' | null };
  options: QuestionOption[];
  explanation: string | null;
  empty: boolean;
  olx: QuestionOlx;
}

/**
 * Every reason the form can be refused. A CLOSED set, mirroring `questions.REASONS`.
 *
 * Closed on purpose, and the client is where it pays off: `REFUSAL_HEADLINES` in
 * `question-refusals.ts` is a `Record` over this union, so a reason added on the server
 * without a sentence here fails the type check rather than reaching an author as a blank
 * dead end.
 */
export type QuestionRefusalReason =
  | 'unparseable'
  | 'not_a_problem'
  | 'no_response'
  | 'multiple_responses'
  | 'unsupported_response'
  | 'stray_content'
  | 'unsupported_group'
  | 'group_attribute'
  | 'response_attribute'
  | 'choice_attribute'
  | 'partial_choice'
  | 'markup_in_text'
  | 'per_option_feedback'
  | 'unsupported_solution'
  | 'demand_hint'
  | 'script'
  | 'comment'
  | 'markdown_present'
  | 'no_options'
  | 'library_component'
  | 'platform_contract';

export interface QuestionRefusal {
  reason: QuestionRefusalReason;
  /** A sentence naming the construct that was found. Written server-side, for a person. */
  detail: string;
}

/**
 * The three settings the form's second half edits, as the block holds them.
 *
 * `weight: null` and `weight: 0` are DIFFERENT and their consequences are opposite — unset
 * means one mark, zero means the question contributes nothing to the grade. Typed as
 * `number | null` rather than `number` so that difference survives the wire; anything that
 * coerced the two together here would make the defect unfixable further up.
 *
 * `showanswer` is never null in practice because its field has a default (`finished`), which
 * is why all 452 problems in this estate report it. `weight` and `max_attempts` have no
 * default, so unset really does arrive as `null`.
 */
export interface QuestionSettings {
  weight: number | null;
  max_attempts: number | null;
  showanswer: string | null;
}

/** One legal `showanswer` value, with upstream's own label. Read off the field, never listed. */
export interface ShowanswerChoice {
  value: string;
  display_name: string;
}

/**
 * What the form sends back.
 *
 * A key is present ONLY when the author changed that setting, and that is a correctness
 * requirement rather than a saving: sending `weight` on every save would write a value to
 * the 22 problems that have never had one, and omitting it when the author has CLEARED a
 * previously-set value would leave the old one in place. So: unchanged means absent, cleared
 * means explicitly `null`.
 */
export interface QuestionMetadata {
  weight?: number | null;
  max_attempts?: number | null;
  showanswer?: string;
  display_name?: string;
}

export interface EditorCapability {
  /**
   * 'studio' — embed the platform's own editor, which handles this type.
   * 'html'   — our editor; `data` is a fragment of HTML.
   * 'olx'    — our editor; `data` is OLX (capa XML and friends).
   * 'video'  — our editor over `fields`; a video keeps NOTHING in `data`.
   */
  kind: EditorKind;
  block_type: string;
  /**
   * The block's editable payload, when it has one.
   *
   * Present for our own editors, and also alongside 'studio' when the block keeps its
   * content in a `data` field — a surface that may look but not edit shows that source
   * rather than embedding an editor whose Save would be refused. Absent for types that
   * keep everything in settings fields (open response, poll, survey, LTI).
   */
  data?: string;
  display_name?: string;
  /**
   * Present only for `kind: 'video'`.
   *
   * A video block's `data` exists but is always empty — everything an author changes lives
   * in settings fields — so a source editor would show a blank box. These are those fields,
   * already JSON-safe (the two RelativeTime ones arrive as "HH:MM:SS", not timedelta).
   */
  fields?: VideoFields;
  /**
   * Present only for `kind: 'question'` — the decoded question, and everything the settings
   * half of the form needs. All three arrive together or not at all, because a form that
   * could show a question but not its marks would be a form that silently drops them.
   */
  question?: Question;
  settings?: QuestionSettings;
  showanswer_choices?: ShowanswerChoice[];
  /**
   * Present when the form was refused. `kind` is then `'olx'` and the raw editor opens — but
   * it opens SAYING WHY, because "here is a textarea" with no explanation is the dead end
   * this whole feature exists to remove.
   */
  question_refusal?: QuestionRefusal;
}

export interface VideoFields {
  edx_video_id?: string;
  /** A video may legitimately have several sources; kept as a list so a save cannot drop any. */
  html5_sources?: string[];
  youtube_id_1_0?: string;
  sub?: string;
  download_video?: boolean;
  show_captions?: boolean;
  start_time?: string;
  end_time?: string;
}

/**
 * A library component. `lb:` is the canonical namespace of a v2 usage key, so this is a
 * statement about the key's own format and not an inference about the block.
 */
const isLibraryBlock = (blockKey: string) => blockKey.startsWith('lb:');

export const getEditor = (blockKey: string) =>
  openedx.get<EditorCapability>(`/api/authoring/v1/editor/${blockKey}`);

/**
 * Where the platform's own editor for this block lives, for a `kind: 'studio'` block.
 *
 * Both are same-origin through the dev proxy, which is not incidental: the CMS session
 * cookie has to reach them (a cross-site iframe would have it stripped as a third-party
 * cookie), and the embed's `frame-ancestors 'self'` has to be satisfied.
 *
 * The library embed renders the block's `studio_view` with a JS runtime whose handler URLs
 * point back at the v2 runtime, which is what makes its own Save button work. Colons are
 * legal in a path segment and Django percent-decodes `PATH_INFO` before resolving, so the
 * key needs no encoding here — the same shape as the probe URL above.
 */
export const editorEmbedUrl = (blockKey: string) =>
  isLibraryBlock(blockKey)
    ? `/xblocks/v2/${blockKey}/embed/studio_view/`
    : editorUrl(blockKey);

/**
 * Save a component's content, and optionally rename it.
 *
 * Both endpoints take the same `{data, metadata}` shape and both create a DRAFT: the unit
 * or the library still has to be published before a learner sees the change. The library
 * one is the v2 runtime's `fields/` view, which is deliberately described upstream as "the
 * exact subset of functionality needed to support the v2 editors" — data and metadata, and
 * that is all we send.
 *
 * `display_name` lives in metadata rather than in the content, on both sides. For a library
 * block it used to be reachable only by hand-editing the `display_name` attribute in raw
 * OLX; naming it as a field is the same thing said properly.
 */
export function saveBlockContent(
  blockKey: string,
  content: { data: string; displayName?: string },
) {
  const payload = {
    data: content.data,
    ...(content.displayName === undefined
      ? {}
      : { metadata: { display_name: content.displayName } }),
  };

  return isLibraryBlock(blockKey)
    ? openedx.post(`/api/xblock/v2/xblocks/${blockKey}/fields/`, payload)
    : openedx.post(`/api/authoring/v1/xblock/${blockKey}`, payload);
}

/**
 * Save a video's fields.
 *
 * Separate from `saveBlockContent` because a video has no `data` to send: its content is
 * entirely settings, so this writes `metadata` only. Sending `data: ""` alongside would be
 * harmless today but would quietly blank the field if a future block type used both.
 *
 * Same endpoint and the same draft semantics as every other save — the unit still has to be
 * published before a learner sees it.
 */
export function saveVideoFields(
  blockKey: string,
  fields: VideoFields,
  displayName?: string,
) {
  const metadata: Record<string, unknown> = { ...fields };
  if (displayName !== undefined) metadata.display_name = displayName;

  return isLibraryBlock(blockKey)
    ? openedx.post(`/api/xblock/v2/xblocks/${blockKey}/fields/`, { metadata })
    : openedx.post(`/api/authoring/v1/xblock/${blockKey}`, { metadata });
}

/**
 * Save a question written in the form.
 *
 * THE FORM SENDS THE QUESTION, NOT THE OLX, and that is the one design decision in this
 * feature the spec left open. It matters enough to write down.
 *
 * The alternative is a second encoder, here, in TypeScript. That would mean two
 * implementations of the one translation this feature consists of — and the whole reason the
 * Python one is trustworthy is that it has been swept against all 452 real problems in the
 * estate, round-tripping 440 of them content-identically and every one of those
 * byte-identically. A TypeScript twin would carry none of that evidence, would have to be
 * re-swept to earn any, and would drift the first time either side was fixed. "A second
 * implementation would be a second thing to get wrong" is the locked rationale for using
 * upstream's own backend for content; it applies with just as much force to our own
 * converter.
 *
 * So the question travels as structure and the server encodes it, on the SAME route, through
 * the SAME handler, behind the SAME gate. This is not a new endpoint or a new write path:
 * `POST /api/authoring/v1/xblock/<key>` is exactly where `saveBlockContent` already writes,
 * `MayManageThisCourse` is already installed on it, and `handle_xblock` still receives
 * `{data, metadata}` — the translation from `question` to `data` happens before it, in
 * `outline.dispatch`. What the decision buys is that the bytes an author's question becomes
 * are produced by the encoder that was measured, and only by that one.
 *
 * COURSE COMPONENTS ONLY, and it throws rather than guessing. A library block's write goes
 * to the v2 runtime's `fields/` endpoint, which is not behind `MayManageThisCourse` — that
 * gate is about course orgs — so a library question's authority model is a different
 * question that this slice does not answer. The server refuses the form for a library block,
 * so this is unreachable through the UI; it is here because a silent write to the wrong
 * endpoint is the failure that would be hardest to notice, and an exception is the cheapest
 * thing that cannot happen quietly.
 */
export function saveQuestion(
  blockKey: string,
  payload: { question?: Question; metadata?: QuestionMetadata },
) {
  if (isLibraryBlock(blockKey)) {
    throw new Error(
      `Refusing to save a question form to the library block ${blockKey}: that write goes ` +
        'to a different endpoint, behind a different permission check.',
    );
  }

  // THE `question` KEY IS ALWAYS SENT, AND MAY BE `null`. Its presence is what tells the
  // server this came from the question form, which is what holds the metadata to the form's
  // own allowlist; its value is what says whether the content should be rewritten. So an
  // author who changes only the marks sends `{question: null, metadata: {weight: 2}}` and
  // the OLX is not rewritten at all — not even re-encoded to the same bytes. That is a
  // stronger guarantee than re-encoding identically, and it is the one an author relies on
  // when they open a question, read it, and close it.
  const body: { question: Question | null; metadata?: QuestionMetadata } = {
    question: payload.question ?? null,
  };
  if (payload.metadata && Object.keys(payload.metadata).length > 0) {
    body.metadata = payload.metadata;
  }

  return openedx.post(`/api/authoring/v1/xblock/${blockKey}`, body);
}
